// Country-first mailing address. US / Canada / Australia keep city + region +
// postal fields. Everyone else writes the address the way it goes on an envelope.

export const MAILING_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IS', name: 'Iceland' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'RO', name: 'Romania' },
  { code: 'GR', name: 'Greece' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'PH', name: 'Philippines' },
  { code: 'IN', name: 'India' },
  { code: 'IL', name: 'Israel' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'OTHER', name: 'Somewhere else' }
]

// Frequent IABF countries first; the rest of the dropdown is A–Z.
export const MAILING_FEATURED_COUNTRY_CODES = ['US', 'GB', 'CA', 'ES', 'DE', 'AU']

export function mailingCountryOptions () {
  const byCode = {}
  MAILING_COUNTRIES.forEach((c) => {
    byCode[c.code] = c
  })
  const featuredCodes = new Set(MAILING_FEATURED_COUNTRY_CODES)
  const featured = []
  MAILING_FEATURED_COUNTRY_CODES.forEach((code) => {
    if (byCode[code]) {
      featured.push(byCode[code])
    }
  })
  const more = MAILING_COUNTRIES.filter((c) => c.code !== 'OTHER' && !featuredCodes.has(c.code))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'en'))
  const other = byCode.OTHER || null
  return { featured, more, other }
}

export function mailingCountrySelectList () {
  const { featured, more, other } = mailingCountryOptions()
  const list = featured.map((c) => ({ code: c.code, name: c.name, disabled: false }))
  list.push({ code: '_divider', name: '────────', disabled: true })
  more.forEach((c) => {
    list.push({ code: c.code, name: c.name, disabled: false })
  })
  if (other) {
    list.push({ code: other.code, name: other.name, disabled: false })
  }
  return list
}

const NAME_ALIASES = {
  USA: 'US',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  UK: 'GB',
  'UNITED KINGDOM': 'GB',
  'GREAT BRITAIN': 'GB',
  ENGLAND: 'GB',
  SCOTLAND: 'GB',
  WALES: 'GB',
  'NORTHERN IRELAND': 'GB',
  CANADA: 'CA',
  AUSTRALIA: 'AU',
  IRELAND: 'IE'
}

const STRUCTURED_CODES = {
  US: true,
  CA: true,
  AU: true
}

export function normalizeCountryCode (raw) {
  const s = String(raw || '').trim()
  if (!s) {
    return ''
  }
  const upper = s.toUpperCase()
  if (NAME_ALIASES[upper]) {
    return NAME_ALIASES[upper]
  }
  if (upper.length === 2) {
    const hit = MAILING_COUNTRIES.find((c) => c.code === upper)
    if (hit) {
      return hit.code
    }
  }
  const byName = MAILING_COUNTRIES.find((c) => c.name.toUpperCase() === upper)
  return byName ? byName.code : s
}

export function countryName (raw) {
  const code = normalizeCountryCode(raw)
  const hit = MAILING_COUNTRIES.find((c) => c.code === code)
  if (hit) {
    return hit.name
  }
  return String(raw || '').trim()
}

export function isStructuredMailingCountry (raw) {
  const code = normalizeCountryCode(raw)
  return !!STRUCTURED_CODES[code]
}

export function regionLabel (raw) {
  const code = normalizeCountryCode(raw)
  if (code === 'US') {
    return 'State'
  }
  if (code === 'CA') {
    return 'Province'
  }
  if (code === 'AU') {
    return 'State / territory'
  }
  return 'State / province'
}

export function postalLabel (raw) {
  const code = normalizeCountryCode(raw)
  if (code === 'US') {
    return 'ZIP code'
  }
  if (code === 'CA') {
    return 'Postal code'
  }
  return 'Postcode'
}

export function mailingAddressComplete (addr) {
  const street = String((addr && addr.addressStreet) || '').trim()
  const country = normalizeCountryCode((addr && addr.addressCountry) || '')
  if (!street || !country) {
    return false
  }
  if (isStructuredMailingCountry(country)) {
    return !!(
      String(addr.addressCity || '').trim() &&
      String(addr.addressState || '').trim() &&
      String(addr.addressZip || '').trim()
    )
  }
  return street.replace(/\s+/g, ' ').length >= 8
}

export function mailingAddressError (addr) {
  const country = normalizeCountryCode((addr && addr.addressCountry) || '')
  if (!country) {
    return 'Please choose a country so we know how to address your mail.'
  }
  if (!String((addr && addr.addressStreet) || '').trim()) {
    return isStructuredMailingCountry(country)
      ? 'Please enter your street address.'
      : 'Please enter your mailing address the way you’d write it on an envelope.'
  }
  if (isStructuredMailingCountry(country) && !mailingAddressComplete(addr)) {
    return 'Please add city, ' + regionLabel(country).toLowerCase() + ', and ' +
      postalLabel(country).toLowerCase() + '.'
  }
  if (!mailingAddressComplete(addr)) {
    return 'Please enter a mailing address we can send a package to.'
  }
  return ''
}

export function formatMailingLines (addr) {
  const country = normalizeCountryCode((addr && addr.addressCountry) || '')
  const name = countryName(addr && addr.addressCountry)
  const street = String((addr && addr.addressStreet) || '').trim()
  const street2 = String((addr && addr.addressStreet2) || '').trim()
  const city = String((addr && addr.addressCity) || '').trim()
  const state = String((addr && addr.addressState) || '').trim()
  const zip = String((addr && addr.addressZip) || '').trim()
  const lines = []

  if (!isStructuredMailingCountry(country)) {
    street.split(/\n+/).forEach((line) => {
      const t = line.trim()
      if (t) {
        lines.push(t)
      }
    })
    if (street2) {
      lines.push(street2)
    }
    const leftover = [city, state, zip].filter(Boolean).join(', ')
    if (leftover && lines.indexOf(leftover) === -1) {
      lines.push(leftover)
    }
    if (name) {
      lines.push(name)
    }
    return lines
  }

  const streetLine = [street, street2].filter(Boolean).join(', ')
  if (streetLine) {
    lines.push(streetLine)
  }
  const cityLine = [city, state, zip].filter(Boolean).join(', ')
  if (cityLine) {
    lines.push(cityLine)
  }
  if (name && name !== 'United States') {
    lines.push(name)
  }
  return lines
}

/** Fold US-style pieces into one envelope block when switching country. */
export function foldIntoEnvelope (addr) {
  const street = String((addr && addr.addressStreet) || '').trim()
  if (street.indexOf('\n') !== -1) {
    return {
      addressStreet: street,
      addressStreet2: '',
      addressCity: '',
      addressState: '',
      addressZip: ''
    }
  }
  const parts = [
    street,
    String((addr && addr.addressStreet2) || '').trim(),
    String((addr && addr.addressCity) || '').trim(),
    [String((addr && addr.addressState) || '').trim(), String((addr && addr.addressZip) || '').trim()]
      .filter(Boolean)
      .join(' ')
  ].filter(Boolean)
  return {
    addressStreet: parts.join('\n'),
    addressStreet2: '',
    addressCity: '',
    addressState: '',
    addressZip: ''
  }
}
